import React, { useEffect, useRef, useState } from "react";
import "ol/ol.css";
import "ol-layerswitcher/dist/ol-layerswitcher.css";
import "../App.css";
import Map from "ol/Map";
import View from "ol/View";
import TileLayer from "ol/layer/Tile";
import { TileWMS, XYZ } from "ol/source";
import { RegularShape, Stroke } from "ol/style";
import OSM from "ol/source/OSM";
import { fromLonLat } from "ol/proj";
import LayerSwitcher from "ol-layerswitcher";
import { ScaleLine, FullScreen, defaults as defaultControls, Control } from "ol/control";
import * as d3 from "d3";
import GeoJSON from "ol/format/GeoJSON";
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import Style from "ol/style/Style";
import Fill from "ol/style/Fill";
import proj4 from "proj4";
import Graph from "./graph";
import api from "../services/api";
import { Button, Input,Spin } from "antd";
import { EyeOutlined, EyeInvisibleOutlined, LoadingOutlined } from "@ant-design/icons";

// Function to get color based on LWE_Thickness value
const getColor = (value, min, max) => {
  const normalizedValue = (value - min) / (max - min);
  const hue = normalizedValue * 240; // Red to Blue
  return `hsl(${hue}, 100%, 50%)`;
};

// Custom Legend Control
class LegendControl extends Control {
  constructor(opt_options) {
    const options = opt_options || {};
    const element = document.createElement("div");
    element.className = "legend ol-unselectable ol-control";
    element.style.backgroundColor = "black";
    element.style.padding = "10px";
    element.style.borderRadius = "5px";
    element.style.boxShadow = "0 2px 4px rgba(0,0,0,0.3)";
    super({ element: element, target: options.target });
    this.minValue = 0;
    this.maxValue = 0;
  }

  setMap(map) {
    super.setMap(map);
    if (map) this.render();
  }

  updateLegend(minValue, maxValue) {
    this.minValue = maxValue;
    this.maxValue = minValue;
    this.render();
  }

  render() {
    const midValue = (this.minValue + this.maxValue) / 2;
    const gradient = `linear-gradient(to bottom, 
      ${getColor(this.minValue, this.minValue, this.maxValue)}, 
      ${getColor(midValue, this.minValue, this.maxValue)}, 
      ${getColor(this.maxValue, this.minValue, this.maxValue)})`;

    this.element.innerHTML = `
      <div style="font-size: 12px; font-weight: bold; margin-bottom: 5px;">Water Equivalent <br/>Thickness (cm)</div>
      <div style="display: flex; align-items: center;">
        <div style="width: 20px; height: 150px; background: ${gradient}; margin-right: 10px;"></div>
        <div style="display: flex; flex-direction: column; justify-content: space-between; height: 150px;">
          <span>${this.maxValue.toFixed(2)}</span>
          <span>${midValue.toFixed(2)}</span>
          <span>${this.minValue.toFixed(2)}</span>
        </div>
      </div>`;
  }
}

function CountryYearSelection() {
  const mapRef = useRef(null);
  const [startYear, setStartYear] = useState("");
  const [endYear, setEndYear] = useState("");
  const [map, setMap] = useState(null);
  const [isVisible, setIsVisible] = useState(true);
  const [vectorLayer, setVectorLayer] = useState(null);
  const [loading, setLoading] = useState(false);
  const [popupContent, setPopupContent] = useState("");
  const [popupStyle, setPopupStyle] = useState({ left: "0px", top: "0px" });
  const sourceProj = "EPSG:3857";
  const destProj = "EPSG:4326";
    const [year, setYear] = useState("");
  
  const fetchGeoJSON = () => {
    if (startYear && endYear && map) {
      setLoading(true);
      api.get("/geojson/", { params: { start: startYear, end: endYear } })
  .then((response) => {
    const data = response.data;
    const features = new GeoJSON().readFeatures(data, { featureProjection: "EPSG:3857" });

    const lweValues = features.map((feature) => feature.get("LWE_Thickness"));
    const minLWE = Math.min(...lweValues);
    const maxLWE = Math.max(...lweValues);

    const vectorSource = new VectorSource({ features });

          if (vectorLayer) {
            map.removeLayer(vectorLayer);
          }

          const newVectorLayer = new VectorLayer({
            source: vectorSource,
            style: (feature) => {
              const value = feature.get("LWE_Thickness");
              return new Style({
                image: new RegularShape({
                  points: 4,
                  radius: 28,
                 
                  angle: 0,
                  fill: new Fill({
                    color: getColor(value, minLWE, maxLWE),
                  }),
                  stroke: new Stroke({
                    color: getColor(value, minLWE, maxLWE),
                    width: 0,
                  }),
                }),
                zIndex: 1, // Lower zIndex for GeoJSON layer

              });
            },
            title: "LWE",
          });

          map.addLayer(newVectorLayer);
          setVectorLayer(newVectorLayer);
 // Add popup interaction
        map.on("singleclick", (evt) => {
          const feature = map.forEachFeatureAtPixel(evt.pixel, (feature) => feature);
          if (feature) {
            const flatCoordinates = [feature.values_.geometry.flatCoordinates[0], feature.values_.geometry.flatCoordinates[1]];
            const [lon, lat] = proj4(sourceProj, destProj, flatCoordinates);

            const properties = feature.getProperties();
            const regionName = properties.year|| year; // Extract region from feature or use selected region
        
           
        
              
            const content = `<u><b>Ethiopia Water Equivalent Thickness(${startYear}-${endYear})</b></u><br/>
            Latitude: <span style="background-color:#848484;color:white">${lat.toFixed(4)}</span><br/>
            Longitude: <span style="background-color:#848484;color:white">${lon.toFixed(4)}</span><br/>
            Water Equivalent Thickness: <span style="background-color:${getColor(properties.LWE_Thickness.toFixed(3), minLWE, maxLWE)};color:white">${properties.LWE_Thickness.toFixed(3)}</span>`;
             setPopupContent(content);

            const coordinate = evt.coordinate;
            const pixel = map.getPixelFromCoordinate(coordinate);
            setPopupStyle({
              left: `${pixel[0]}px`,
              top: `${pixel[1]}px`,
            });

            const popupElement = document.getElementById("popup");
            popupElement.classList.add("active");
          }
        });

        map.on("click", () => {
          const popupElement = document.getElementById("popup");
          popupElement.classList.remove("active");
        });
          const legendControl = map.getControls().getArray().find((control) => control instanceof LegendControl);
          if (legendControl) legendControl.updateLegend(minLWE, maxLWE);
        })
        .catch((error) => console.error("Error fetching GeoJSON:", error))
        .finally(() => setLoading(false));
    }
  };

  useEffect(() => {
    if (!mapRef.current) return;

    const initialMap = new Map({
      controls: defaultControls().extend([new FullScreen(), new LegendControl()]),
      target: mapRef.current,
      layers: [
        new TileLayer({ source: new OSM(), title: "OSM", visible: false }),
        new TileLayer({ source: new XYZ({ url: "https://mt1.google.com/vt/lyrs=s&x={x}&y={y}&z={z}" }), title: "Satellite", visible: true }),
        new TileLayer({
          source: new TileWMS({
            url: "http://196.188.116.41:8082/geoserver/geoserver/wms",
            params: { LAYERS: "geoserver:eth_aoi", TILED: true },
            serverType: "geoserver",
            visible: true,
            transition: 0,
          }),
          title: "Ethiopia",
          visible: true,
          opacity: 0.8,
          zIndex: 2, // Higher zIndex to ensure it's on top of the GeoJSON layer
        }),
      ],
      view: new View({
        center: fromLonLat([38.971861469308, 8.748207216169536]),
       
       zoom: 6.5,
        minZoom: 5.5,
        maxZoom: 8,
      }),  
    
    });

    initialMap.addControl(new LayerSwitcher({ reverse: true, activationMode: "click", groupSelectStyle: "children" }));
    initialMap.addControl(new ScaleLine({ units: "metric" }));

    setMap(initialMap);
    return () => initialMap.dispose();
  }, []);
  const close = () => {
    const popupElement = document.getElementById("popup");
    if (popupElement) {
      popupElement.classList.remove("active");
    }
    return false;
  };
  return (
    <>
     <div>
      <Button
      className="annualSelection"
        type="default"
        icon={isVisible ? <EyeInvisibleOutlined /> : <EyeOutlined />}
        onClick={() => setIsVisible(!isVisible)}
        style={{ float: "left",zIndex: '2000'
        }}
      >
        {isVisible ? "Hide " : "Show "}
      </Button>

      {isVisible && (
        <div className="annualSelection">
          <label>Start Year:</label>
          <input
            type="number"
            placeholder="Start Year"
            onChange={(e) => setStartYear(e.target.value)}
            min="2002"
            max="2024"
            step="1"
          />
          
          <label className="end_year">End Year:</label>
          <input
            type="number"
            placeholder="End Year"
            onChange={(e) => setEndYear(e.target.value)}
            min="2002"
            max="2024"
            step="1"
          />
          
          <Button
            type="primary"
            onClick={fetchGeoJSON}
            loading={loading}
            icon={loading ? <LoadingOutlined /> : null}
            style={{ marginLeft: "5px" }}
          >
            {loading ? "Loading..." : "Submit"}
          </Button>

          <div className="date_calculate">
            <span>Ethiopia WET From {startYear}</span> to <span>{endYear}</span>
          </div>
        </div>
      )}
    </div>

      {loading && (
        <div className="loading-overlay">
          <div className="spinner"></div>
          <p>Loading data...</p>
        </div>
      )}

      <div className="main-container">
      <div id="popup" className="ol-popup">
        <a href="#" id="popup-closer" className="ol-popup-closer" onClick={close}><Button>Close</Button> </a><br/>
          <div id="popup-content" dangerouslySetInnerHTML={{ __html: popupContent }}></div>
        </div>
        <div id="map" className="map" ref={mapRef} style={{ width: "100%", height: "100vh" }}></div>
        
      </div>
    </>
  );
}

export default CountryYearSelection;
